import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Upload, ImagePlus, Loader2, CheckCircle2, XCircle, MessageSquare, Mail, Hand, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { compressImage } from '../utils/compressImage';
import { uploadJobPhotoDirect } from '../utils/directUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PHOTOS = 10;
const MAX_SIZE = 25 * 1024 * 1024;
const ALLOWED_EXT = ['.jpg', '.jpeg', '.png', '.heic', '.heif', '.webp'];
const VIA = [
  { value: 'text', label: 'Text message', Icon: MessageSquare },
  { value: 'email', label: 'Email', Icon: Mail },
  { value: 'in_person', label: 'In person / USB', Icon: Hand },
  { value: 'other', label: 'Other', Icon: HelpCircle },
];
export const RECEIVED_VIA_LABEL = Object.fromEntries(VIA.map((v) => [v.value, v.label]));

/* Admin / supervisor uploader: drag-and-drop or browse, many files at once,
   optional "taken by" technician name + how the photos arrived (Nov 2026). */
export default function AdminPhotoUploader({ job, token, users = null, onUploaded, compact = false }) {
  const [dragOver, setDragOver] = useState(false);
  const [takenBy, setTakenBy] = useState('');
  const [via, setVia] = useState('');
  const [queue, setQueue] = useState([]); // {name, pct, status: 'pending'|'uploading'|'done'|'error', error}
  const [busy, setBusy] = useState(false);
  const [names, setNames] = useState(users ? users.map((u) => u.name).filter(Boolean) : []);
  const inputRef = useRef(null);
  const count = job?.photos?.length || 0;
  const remaining = Math.max(0, MAX_PHOTOS - count);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (users) { setNames(users.map((u) => u.name).filter(Boolean)); return; }
    let alive = true;
    axios.get(`${API}/users`, { headers }).then(({ data }) => {
      if (alive && Array.isArray(data)) setNames(data.map((u) => u.name).filter(Boolean));
    }).catch(() => {});
    return () => { alive = false; };
  }, [users, token]);

  const uploadOne = async (rawFile, i) => {
    const setItem = (patch) => setQueue((q) => q.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
    const dot = rawFile.name.lastIndexOf('.');
    const ext = dot >= 0 ? rawFile.name.slice(dot).toLowerCase() : '';
    if (!ALLOWED_EXT.includes(ext)) { setItem({ status: 'error', error: 'Only JPG, PNG, HEIC, HEIF or WEBP' }); return null; }
    if (rawFile.size > MAX_SIZE) { setItem({ status: 'error', error: `${(rawFile.size / 1048576).toFixed(1)} MB exceeds 25 MB` }); return null; }
    setItem({ status: 'uploading', pct: 0 });
    try {
      const file = await compressImage(rawFile);
      const meta = { taken_by: takenBy.trim() || undefined, received_via: via || undefined };
      let data;
      try {
        data = await uploadJobPhotoDirect({ apiBase: API, jobId: job.id, token, file, originalName: rawFile.name, onProgress: (pct) => setItem({ pct }), extra: meta });
      } catch (directErr) {
        const st = directErr?.response?.status;
        if (st === 400 || st === 403) throw directErr;
        const fd = new FormData();
        fd.append('file', file);
        if (meta.taken_by) fd.append('taken_by', meta.taken_by);
        if (meta.received_via) fd.append('received_via', meta.received_via);
        const res = await axios.post(`${API}/jobs/${job.id}/upload-photo`, fd, {
          headers, timeout: 300000,
          onUploadProgress: (e) => { if (e.total) setItem({ pct: Math.round((e.loaded / e.total) * 100) }); },
        });
        data = res.data;
      }
      setItem({ status: 'done', pct: 100 });
      return data?.photo || null;
    } catch (e) {
      setItem({ status: 'error', error: e.response?.data?.detail || e.message || 'Upload failed' });
      return null;
    }
  };

  const handleFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f && f.name);
    if (!files.length || busy) return;
    if (remaining === 0) { toast.error(`This job already has ${MAX_PHOTOS} photos (the maximum).`); return; }
    const batch = files.slice(0, remaining);
    if (files.length > remaining) toast.warning(`Only ${remaining} slot${remaining === 1 ? '' : 's'} left — uploading the first ${remaining}.`);
    setBusy(true);
    setQueue(batch.map((f) => ({ name: f.name, pct: 0, status: 'pending' })));
    const photos = [];
    for (let i = 0; i < batch.length; i += 1) {
      // sequential: keeps per-file progress readable and avoids hammering S3 from an office PC
      const p = await uploadOne(batch[i], i); // eslint-disable-line no-await-in-loop
      if (p) photos.push(p);
    }
    setBusy(false);
    if (photos.length) {
      toast.success(`${photos.length} photo${photos.length === 1 ? '' : 's'} added to ${job.title || 'the job'}${takenBy.trim() ? ` (taken by ${takenBy.trim()})` : ''}.`);
      onUploaded?.(photos);
    }
  }, [busy, remaining, takenBy, via, job?.id, job?.title, token, onUploaded]);

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    handleFiles(e.dataTransfer?.files);
  };

  return (
    <div className="space-y-3" data-testid="admin-photo-uploader">
      <div className={`grid gap-3 ${compact ? 'sm:grid-cols-2' : 'sm:grid-cols-2'}`}>
        <div>
          <Label className="text-xs text-slate-300">Photos taken by <span className="text-slate-500">(optional)</span></Label>
          <Input list="admin-photo-taker-names" value={takenBy} onChange={(e) => setTakenBy(e.target.value)} placeholder="Technician / worker name"
                 className="h-9 mt-1" data-testid="admin-photo-taken-by" disabled={busy} />
          <datalist id="admin-photo-taker-names">{names.map((n) => <option key={n} value={n} />)}</datalist>
        </div>
        <div>
          <Label className="text-xs text-slate-300">How did you receive them? <span className="text-slate-500">(optional)</span></Label>
          <div className="flex flex-wrap gap-1.5 mt-1" data-testid="admin-photo-received-via">
            {VIA.map(({ value, label, Icon }) => (
              <button key={value} type="button" disabled={busy} onClick={() => setVia(via === value ? '' : value)}
                      className={`inline-flex items-center gap-1 text-xs px-2 py-1.5 rounded-md border transition-colors ${via === value ? 'bg-[#1E88E5] border-[#1E88E5] text-white' : 'border-[#2D3748] text-slate-300 hover:border-[#1E88E5]'}`}
                      data-testid={`admin-photo-via-${value}`}>
                <Icon className="w-3 h-3" /> {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (!busy) setDragOver(true); }}
        onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); if (!busy) setDragOver(true); }}
        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); }}
        onDrop={onDrop}
        onClick={() => { if (!busy && remaining > 0) inputRef.current?.click(); }}
        role="button" tabIndex={0}
        onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !busy && remaining > 0) inputRef.current?.click(); }}
        aria-disabled={busy || remaining === 0}
        className={`rounded-xl border-2 border-dashed text-center transition-colors cursor-pointer ${compact ? 'p-5' : 'p-8'} ${dragOver ? 'border-[#1E88E5] bg-[#1E88E5]/10' : 'border-[#2D3748] hover:border-[#1E88E5]/70'} ${busy || remaining === 0 ? 'opacity-70 cursor-not-allowed' : ''}`}
        data-testid="admin-photo-dropzone"
      >
        <input ref={inputRef} type="file" accept="image/*,.heic,.heif" multiple className="hidden" disabled={busy || remaining === 0}
               onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }} data-testid="admin-photo-input" />
        {busy ? <Loader2 className="w-9 h-9 mx-auto mb-2 text-[#1E88E5] animate-spin" /> : dragOver ? <ImagePlus className="w-9 h-9 mx-auto mb-2 text-[#1E88E5]" /> : <Upload className="w-9 h-9 mx-auto mb-2 text-slate-400" />}
        <p className="text-sm font-semibold text-slate-200">
          {remaining === 0 ? `This job has the maximum of ${MAX_PHOTOS} photos` : dragOver ? 'Drop the photos here' : 'Drag & drop photos here, or click to choose from your computer'}
        </p>
        <p className="text-xs text-slate-500 mt-1" data-testid="admin-photo-slots">
          JPG, PNG, HEIC or WEBP · up to 25 MB each, auto-compressed · {remaining} of {MAX_PHOTOS} slot{remaining === 1 ? '' : 's'} left
        </p>
      </div>

      {queue.length > 0 && (
        <ul className="space-y-1.5" data-testid="admin-photo-queue">
          {queue.map((it, i) => (
            <li key={`${it.name}-${i}`} className="text-xs" data-testid={`admin-photo-queue-item-${i}`}>
              <div className="flex items-center justify-between gap-2 text-slate-300">
                <span className="truncate flex items-center gap-1.5">
                  {it.status === 'done' && <CheckCircle2 className="w-3.5 h-3.5 text-[#00C896]" />}
                  {it.status === 'error' && <XCircle className="w-3.5 h-3.5 text-red-400" />}
                  {it.status === 'uploading' && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#1E88E5]" />}
                  {it.name}
                </span>
                <span className={it.status === 'error' ? 'text-red-400' : 'text-slate-500'}>{it.status === 'error' ? it.error : it.status === 'done' ? 'Added' : it.status === 'uploading' ? `${it.pct}%` : 'Waiting'}</span>
              </div>
              {it.status === 'uploading' && (
                <div className="h-1 rounded-full bg-[#2D3748] overflow-hidden mt-1"><div className="h-full bg-[#1E88E5] rounded-full transition-all" style={{ width: `${it.pct}%` }} /></div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
